/**
 * Invilus Cookie Consent Manager
 *
 * Storage: localStorage key 'invilus-consent-v1'
 * Categories: necessary (always true), analytics, marketing
 * Public API: window.InvilusConsent.show() / .get() / .has(category)
 *
 * Bump the storage key when the schema changes (e.g. v2 with a new category).
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'invilus-consent-v1';
  var BANNER_ID = 'invilus-cookie-banner';

  var defaultDecision = {
    necessary: true,
    analytics: false,
    marketing: false,
    ts: 0
  };

  function readDecision() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (typeof parsed !== 'object' || parsed === null) return null;
      return {
        necessary: true,
        analytics: parsed.analytics === true,
        marketing: parsed.marketing === true,
        ts: typeof parsed.ts === 'number' ? parsed.ts : 0
      };
    } catch (err) {
      return null;
    }
  }

  function writeDecision(decision) {
    try {
      var payload = {
        necessary: true,
        analytics: decision.analytics === true,
        marketing: decision.marketing === true,
        ts: Date.now()
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      return payload;
    } catch (err) {
      return null;
    }
  }

  function dispatchChange(decision) {
    document.dispatchEvent(new CustomEvent('invilus:consent-changed', {
      detail: decision
    }));
  }

  /**
   * Bridge to Shopify Customer Privacy API.
   * Tells Shopify Web Pixels (Meta, GA4, etc.) whether tracking is allowed.
   * Without this call Shopify assumes there is no banner and loads everything.
   * Defensive: API may not be ready at first call; retry once after 500 ms.
   */
  function setShopifyTrackingConsent(decision, attempt) {
    var api = window.Shopify && window.Shopify.customerPrivacy;
    if (!api || typeof api.setTrackingConsent !== 'function') {
      if (!attempt) {
        setTimeout(function () {
          setShopifyTrackingConsent(decision, 1);
        }, 500);
      }
      return;
    }
    var payload = {
      analytics: decision.analytics === true,
      marketing: decision.marketing === true,
      preferences: decision.marketing === true,
      sale_of_data: decision.marketing === true
    };
    try {
      api.setTrackingConsent(payload, function () {});
    } catch (err) {
      // swallow — banner stays functional even if API rejects
    }
  }

  function showBanner() {
    var banner = document.getElementById(BANNER_ID);
    if (!banner) return;
    banner.hidden = false;
    banner.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(function () {
      banner.classList.add('is-visible');
    });
  }

  function hideBanner() {
    var banner = document.getElementById(BANNER_ID);
    if (!banner) return;
    banner.classList.remove('is-visible');
    banner.setAttribute('aria-hidden', 'true');
    setTimeout(function () {
      banner.hidden = true;
    }, 240);
  }

  function setToggle(input, isChecked) {
    if (!input) return;
    input.checked = isChecked;
    var label = input.closest('.invilus-cookie-banner__toggle');
    if (!label) return;
    var stateLabel = label.querySelector('[data-toggle-state]');
    if (stateLabel) stateLabel.textContent = isChecked ? 'an' : 'aus';
    label.classList.toggle('is-active', isChecked);
  }

  function applyDecisionToUI(decision) {
    var analytics = document.querySelector('[data-consent-input="analytics"]');
    var marketing = document.querySelector('[data-consent-input="marketing"]');
    setToggle(analytics, decision.analytics === true);
    setToggle(marketing, decision.marketing === true);
  }

  function readToggles() {
    var analytics = document.querySelector('[data-consent-input="analytics"]');
    var marketing = document.querySelector('[data-consent-input="marketing"]');
    return {
      necessary: true,
      analytics: !!(analytics && analytics.checked),
      marketing: !!(marketing && marketing.checked)
    };
  }

  function toggleDetailsView(forceOpen) {
    var details = document.getElementById('invilus-cookie-details');
    var defaultActions = document.querySelector('[data-default-actions]');
    var saveActions = document.querySelector('[data-save-actions]');
    var trigger = document.querySelector('[data-consent-action="toggle-details"]');
    if (!details) return;
    var willOpen = typeof forceOpen === 'boolean' ? forceOpen : details.hidden;
    details.hidden = !willOpen;
    if (defaultActions) defaultActions.hidden = willOpen;
    if (saveActions) saveActions.hidden = !willOpen;
    if (trigger) trigger.setAttribute('aria-expanded', String(willOpen));
  }

  function bind() {
    var banner = document.getElementById(BANNER_ID);
    if (!banner) return;

    banner.addEventListener('click', function (evt) {
      var target = evt.target.closest('[data-consent-action]');
      if (!target) return;
      var action = target.getAttribute('data-consent-action');
      switch (action) {
        case 'accept-all':
          var allYes = writeDecision({ analytics: true, marketing: true });
          if (allYes) {
            applyDecisionToUI(allYes);
            dispatchChange(allYes);
            setShopifyTrackingConsent(allYes);
            hideBanner();
          }
          break;
        case 'reject-all':
          var allNo = writeDecision({ analytics: false, marketing: false });
          if (allNo) {
            applyDecisionToUI(allNo);
            dispatchChange(allNo);
            setShopifyTrackingConsent(allNo);
            hideBanner();
          }
          break;
        case 'toggle-details':
          toggleDetailsView();
          break;
        case 'save':
          var current = readToggles();
          var saved = writeDecision(current);
          if (saved) {
            dispatchChange(saved);
            setShopifyTrackingConsent(saved);
            hideBanner();
          }
          break;
      }
    });

    banner.addEventListener('change', function (evt) {
      var input = evt.target.closest('[data-consent-input]');
      if (!input) return;
      setToggle(input, input.checked);
    });
  }

  function init() {
    bind();
    var decision = readDecision();
    if (decision) {
      applyDecisionToUI(decision);
      dispatchChange(decision);
      setShopifyTrackingConsent(decision);
    } else {
      applyDecisionToUI(defaultDecision);
      // Pre-decision: explicitly tell Shopify nothing is allowed yet,
      // so Web Pixels do not fall back to "no banner present" defaults.
      setShopifyTrackingConsent(defaultDecision);
      showBanner();
    }
  }

  window.InvilusConsent = {
    show: function () {
      var decision = readDecision() || defaultDecision;
      applyDecisionToUI(decision);
      toggleDetailsView(false);
      showBanner();
    },
    get: function () {
      return readDecision() || Object.assign({}, defaultDecision);
    },
    has: function (category) {
      var decision = readDecision();
      if (!decision) return false;
      return decision[category] === true;
    },
    reset: function () {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch (err) {}
      window.InvilusConsent.show();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
